import { Box, Button, Checkbox, Chip, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { LinkToCompany } from 'shared/ui/links/link-to-company';
import { GridLayout } from 'shared/ui/grid-layout';
import { CompanyQueryType, CompanyWithManagersDto, IPagingData, MediaInfoDto } from '@sr/dto';
import { IRemotePagedCollection } from 'utils/remote-paged-collection.hook';
import { ManagerUrlParamsFilter } from 'entities/filters/manager-url-params-filter';
import { LinkedManagerChip } from 'entities/user/linked-manager-chip';
import { YearUrlParamsFilter } from 'entities/filters/year-url-params-filter';
import { ReactElement, useCallback, useMemo } from 'react';
import { Link } from 'shared/ui/link-base';
import { MediaListFilter } from 'features/media/media-filter';
import { CityParamFilter } from '../../entities/geo/cities/city-filter/city-param-filter';
import { OkvedParamFilter } from 'entities/okved/okved-filter/okved-param-filter';
import { EntityTags } from 'entities/tag/ui/entity-tags';
import { TagUrlParamFilter } from 'entities/filters/tags-url-params-filter';
import { DateUrlParamsFilter } from 'entities/filters/date-url-params-filter';
import { formatShortDate } from 'utils/date-format-helpers';
import { FilterWrapper, ValueFormatterContext } from 'shared/ui/filter/filter-wrapper';
import { useManagersDropdownList } from 'features/user/user-picker/ui';
import { useCitiesDropdown } from 'entities/geo/cities/city-picker/hook';
import { useOkvedDropdown } from 'entities/okved/okved-picker/hook';
import { useTags } from 'entities/tag/lib/tags.hook';
import { MoreMenu, useMoreMenu } from 'shared/ui/more-menu';
import { CompanyDuplicatesFilter, COMPANY_DUPLICATES_OPTIONS } from './company-duplicates-filter';

const columns = ['Название', 'Менеджеры', 'Города', 'ОКВЭД', 'Дата последнего контакта', 'Теги'];
const supplierColumns = ['Название поставщика', 'Медиа', 'Тип медиа', 'Город', 'Теги'];

export type BaseProps = {
  companiesCollection: IRemotePagedCollection<CompanyWithManagersDto>;
  paging?: IPagingData;
  onChangePaging?: (paging: IPagingData) => void;
  type: CompanyQueryType;
  duplicatesOnly?: boolean;
};

type SelectableListOptions =
  | {
      onCheckboxChange: (id: number) => void;
      selectedCompaniesIds: number[];
    }
  | {
      onCheckboxChange?: undefined;
      selectedCompaniesIds?: undefined;
    };

type Props = BaseProps & SelectableListOptions;

type FilterProps = {
  manager?: boolean;
  city?: boolean;
  okved?: boolean;
  tags?: boolean;
  year?: boolean;
  eventDate?: boolean;
  duplicatesOnly?: boolean;
  totalCount?: number;
  children?: ReactElement;
};

const BasicFilterLayout = ({ manager, city, okved, tags, year, eventDate, duplicatesOnly, totalCount, children }: FilterProps) => {
  const { items: managers } = useManagersDropdownList();
  const { list: cities } = useCitiesDropdown(true);
  const { items: okvedItems } = useOkvedDropdown();
  const { items: tagItems } = useTags();

  const managerFormatter = useCallback(
    ({ value }: ValueFormatterContext) => {
      const option = managers.find((item) => String(item.id) === value);
      return option ? `${option.firstName} ${option.secondName || ''}`.trim() : value;
    },
    [managers]
  );

  const cityFormatter = useCallback(
    ({ value }: ValueFormatterContext) => {
      const option = cities.find((item) => String(item.id) === value);
      return option ? option.name : value;
    },
    [cities]
  );

  const okvedFormatter = useCallback(
    ({ value }: ValueFormatterContext) => {
      const option = okvedItems.find((item) => String(item.id) === value);
      return option ? `${option.code} - ${option.name}` : value;
    },
    [okvedItems]
  );

  const tagFormatter = useCallback(
    ({ value }: ValueFormatterContext) => {
      const option = tagItems.find((item) => String(item.id) === value);
      return option ? option.name : value;
    },
    [tagItems]
  );

  return (
    <FilterWrapper title="Фильтры компаний" totalCount={totalCount}>
      {manager && (
        <FilterWrapper.Item label="Менеджер" queryKeys="managerId" valueFormatter={managerFormatter}>
          <ManagerUrlParamsFilter queryParamName="managerId" />
        </FilterWrapper.Item>
      )}

      {city && (
        <FilterWrapper.Item label="Город" queryKeys="cityId" valueFormatter={cityFormatter}>
          <CityParamFilter queryParamName={'cityId'} />
        </FilterWrapper.Item>
      )}

      {year && (
        <FilterWrapper.Item label="Год" queryKeys="year">
          <YearUrlParamsFilter queryParamName={'year'} />
        </FilterWrapper.Item>
      )}

      {okved && (
        <FilterWrapper.Item label="ОКВЭД" queryKeys="okvedIds" valueFormatter={okvedFormatter}>
          <OkvedParamFilter queryParamName={'okvedIds'} customSize={1} multi label="ОКВЭД" />
        </FilterWrapper.Item>
      )}

      {eventDate && (
        <FilterWrapper.Item label="Дата последнего контакта" queryKeys="endEventDate">
          <DateUrlParamsFilter queryParamName={'endEventDate'} label="Дата последнего контакта" />
        </FilterWrapper.Item>
      )}

      {children}

      {!duplicatesOnly && <FilterWrapper.Item
        label="Дубли компаний"
        queryKeys="hasDuplicates"
        valueFormatter={({ value }) => COMPANY_DUPLICATES_OPTIONS.find((option) => option.value === value)?.label || value}
      >
        <CompanyDuplicatesFilter />
      </FilterWrapper.Item>}

      {tags && (
        <FilterWrapper.Item label="Теги" queryKeys="tagsIds" valueFormatter={tagFormatter}>
          <TagUrlParamFilter queryParamName="tagsIds" />
        </FilterWrapper.Item>
      )}
    </FilterWrapper>
  );
};

const FilterLayout = ({ queryType, totalCount, duplicatesOnly }: {queryType?: CompanyQueryType; totalCount?: number; duplicatesOnly?: boolean}) => {
  if (queryType === CompanyQueryType.CLIENTS_MY) return <BasicFilterLayout city okved eventDate tags totalCount={totalCount} />;
  if (queryType === CompanyQueryType.CLIENTS_FREE) return <BasicFilterLayout city okved eventDate tags totalCount={totalCount} />;
  if (queryType === CompanyQueryType.CLIENTS_FREE_WITH_INVOICES || queryType === CompanyQueryType.CLIENTS_WITH_LEADS)
    return <BasicFilterLayout city okved year eventDate tags totalCount={totalCount} />;
  if (queryType === CompanyQueryType.SUPPLIERS_ALL) return <MediaListFilter />;

  return <BasicFilterLayout city okved eventDate tags manager totalCount={totalCount} duplicatesOnly={duplicatesOnly} />;
};

export const CompaniesList = ({ companiesCollection, paging, onChangePaging, type, onCheckboxChange, selectedCompaniesIds, duplicatesOnly = false }: Props) => {
  const rowExtraProps: SelectableListOptions = onCheckboxChange ? { onCheckboxChange, selectedCompaniesIds } : { onCheckboxChange: undefined, selectedCompaniesIds: undefined };
  const getColumns = useMemo(() => {
    return type === CompanyQueryType.SUPPLIERS_ALL ? supplierColumns : columns;
  }, [type]);

  return (
    <>
      <Box mb={1}>
        { type && <FilterLayout queryType={type} totalCount={companiesCollection.collection.totalItemsCount} duplicatesOnly={duplicatesOnly} /> }
      </Box>
      <GridLayout<CompanyWithManagersDto>
        columns={onCheckboxChange ? ['', ...getColumns] : getColumns}
        items={companiesCollection.collection.items}
        pagingData={paging}
        onChangePaging={onChangePaging}
        totalItemsCount={companiesCollection.collection.totalItemsCount}
        isLoading={companiesCollection.isLoading}
        error={companiesCollection.error}
        itemRender={(item) => <CompanyRow key={item.id} {...rowExtraProps} companyData={item} isSupplier={type === CompanyQueryType.SUPPLIERS_ALL} showDuplicates={duplicatesOnly} />}
        noItemsText="Список фирм пуст"
      />
    </>
  );
};

type RowProps = {
  companyData: CompanyWithManagersDto;
  isSupplier?: boolean;
  showDuplicates?: boolean;
};

const CompanyRow = ({ companyData, onCheckboxChange, selectedCompaniesIds = [], isSupplier = false, showDuplicates = false }: RowProps & SelectableListOptions) => {
  const { menuData, openMenu } = useMoreMenu( companyData.media ?
    companyData.media?.map((item) => ({ element:<Link to={`/media/${item.id}/home`} key={item.id}>{item.name}</Link> })) : []
  );

  const mediaTypesUniq = companyData.media ? [...new Map(companyData.media.map(x => [x.mediaType.id, x.mediaType])).values()] : [];
  const mediaLen = companyData.media ? companyData.media.length : 0;
  return (
    <>
      <TableRow key={companyData.id}>
        {onCheckboxChange ? (
          <TableCell width={50} component="th" scope="row">
            <Checkbox checked={(selectedCompaniesIds || []).includes(companyData.id)} onChange={() => onCheckboxChange(companyData.id)} />
          </TableCell>
        ) : null}
        <TableCell component="th" scope="row">
          <LinkToCompany company={companyData} />
          {!isSupplier && showDuplicates && !!companyData.duplicateCompanies?.length && (
            <Stack direction="row" gap={0.5} flexWrap="wrap" mt={0.5}>
              <Typography variant="caption" color="text.secondary">Дубликаты:</Typography>
              {companyData.duplicateCompanies.map((company) => (
                <LinkToCompany key={company.id} company={company}>
                  <Chip label={company.name} size="small" sx={{ cursor: 'pointer' }} />
                </LinkToCompany>
              ))}
            </Stack>
          )}
        </TableCell>
        {isSupplier ? (
          <TableCell>
            <Stack direction='row' gap={.5}>

              {companyData.media?.slice(0, 1).map((item) => (
                <Link to={`/media/${item.id}/home`} key={item.id}>
                  {item.name}
                </Link>
              ))}
              { mediaLen > 2 &&
              <>
                <Typography  onClick={(e) => openMenu(e.currentTarget)}>{`и ещё ${mediaLen - 2}`}</Typography>
                <MoreMenu {...menuData} />
              </>
              }
            </Stack>
          </TableCell>
        ) : (
          <TableCell>{companyData.managers.length === 0 ? <>-</> : companyData.managers.map((manager) => <LinkedManagerChip key={manager.id} manager={manager} />)}</TableCell>
        )}
        {isSupplier ? <TableCell>
          <Stack direction='row' gap={0.5}>

            {mediaTypesUniq.map((item) => <Link to={`/settings/media-types/${item.id}`}>{item.name}</Link>)}
          </Stack>
        </TableCell> : null}
        {companyData?.city?.id ? <TableCell>{companyData?.city?.name}</TableCell> : <TableCell>-</TableCell>}

        {
          !isSupplier ? <>
            <TableCell>
              {companyData.okved && companyData.okved.length > 0 ? (
                companyData.okved.map(x => (
                  <div key={x.id}>
                    {x.code} - {x.name}
                  </div>
                ))
              ) : (
                <>-</>
              )}
            </TableCell>

            <TableCell>
              {companyData.lastEventDate ? formatShortDate(companyData.lastEventDate) : '-'}
            </TableCell>
          </> : null
        }
        <TableCell>
          {companyData.tags && <EntityTags tags={companyData.tags}/>}
        </TableCell>
      </TableRow>
    </>
  );
};
